import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { ArrowLeftRight, Armchair, LocateFixed, Lock, UserMinus } from 'lucide-react';
import { FloorplanAssignment, FloorplanEmployee, FloorplanLevel } from '../../types/floorplan';
import { Employee } from '../../store/slices/employeeSlice';
import { Department } from '../../store/slices/departmentSlice';
import { IconTile, PersonAvatar, PersonCell, StatusPill } from '../ui';
import { sbg } from '../../theme/sbgTokens';
import { deskLabel, floorNamesWhenMixed } from './floorplan-panels/deskLabel';

const filterManagers = createFilterOptions<Employee>({
  stringify: (manager) => `${manager.name} ${manager.teamName || ''} ${manager.department?.name || ''} ${manager.email}`,
});

export interface PlacementUpdatePayload {
  managerEmployeeId: string | null;
  managerName: string | null;
  managerUserId: string | null;
  teamName: string | null;
  department: string | null;
}

interface EmployeeDetailDialogProps {
  open: boolean;
  employee: FloorplanEmployee | null;
  assignment: FloorplanAssignment | null;
  managerCandidates: Employee[];
  departments: Department[];
  canEditPlacement: boolean;
  placementSaving?: boolean;
  /** Optional: the layout's floors, so the current desk on a multi-floor layout names its floor. */
  levels?: FloorplanLevel[];
  onClose: () => void;
  onLocate: () => void;
  onChangeDesk: () => void;
  onRemove: () => Promise<void>;
  onSavePlacement: (payload: PlacementUpdatePayload) => Promise<void>;
}

const getDepartmentId = (department: Employee['department'] | FloorplanEmployee['department'] | null | undefined) => {
  if (!department) return '';
  return '_id' in department ? department._id : department.id;
};

const findCurrentManager = (employee: FloorplanEmployee, managerCandidates: Employee[]) => (
  managerCandidates.find((candidate) => candidate.userId && candidate.userId === employee.managerUserId)
  || managerCandidates.find((candidate) => employee.managerName
    && candidate.name.trim().toLowerCase() === employee.managerName.trim().toLowerCase())
);

const EmployeeDetailDialog: React.FC<EmployeeDetailDialogProps> = ({
  open,
  employee,
  assignment,
  managerCandidates,
  departments,
  canEditPlacement,
  placementSaving = false,
  levels,
  onClose,
  onLocate,
  onChangeDesk,
  onRemove,
  onSavePlacement,
}) => {
  const [managerEmployeeId, setManagerEmployeeId] = useState('');
  const [managerName, setManagerName] = useState('');
  const [managerUserId, setManagerUserId] = useState<string | null>(null);
  const [teamName, setTeamName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [showDepartment, setShowDepartment] = useState(false);
  const [initialPlacement, setInitialPlacement] = useState<PlacementUpdatePayload | null>(null);
  const [pendingAction, setPendingAction] = useState<'save' | 'remove' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef(false);
  const initializedEmployeeIdRef = useRef<string | null>(null);
  const busy = placementSaving || pendingAction !== null;

  const deskFloorName = useMemo(
    () => (assignment ? floorNamesWhenMixed(levels).get(assignment.desk.levelId) ?? null : null),
    [assignment, levels]
  );

  const managerById = useMemo(() => new Map(managerCandidates.map((candidate) => [candidate._id, candidate])), [managerCandidates]);

  useEffect(() => {
    if (!open || !employee) {
      initializedEmployeeIdRef.current = null;
      return;
    }
    // Roster refreshes must not overwrite a draft in progress.
    if (initializedEmployeeIdRef.current === employee.id) return;

    const currentManager = findCurrentManager(employee, managerCandidates);
    const placement = {
      managerEmployeeId: currentManager?._id || null,
      managerName: employee.managerName || currentManager?.name || null,
      managerUserId: employee.managerUserId || currentManager?.userId || null,
      teamName: employee.teamName || null,
      department: getDepartmentId(employee.department) || null,
    };
    initializedEmployeeIdRef.current = employee.id;
    setManagerEmployeeId(placement.managerEmployeeId || '');
    setManagerName(placement.managerName || '');
    setManagerUserId(placement.managerUserId);
    setTeamName(placement.teamName || '');
    setDepartmentId(placement.department || '');
    setInitialPlacement({ ...placement, managerName: placement.managerName?.trim() || null, teamName: placement.teamName?.trim() || null });
    setShowDepartment(false);
    setError(null);
  }, [employee, managerCandidates, open]);

  const payload: PlacementUpdatePayload = {
    managerEmployeeId: managerEmployeeId || null,
    managerName: managerName.trim() || null,
    managerUserId,
    teamName: teamName.trim() || null,
    department: departmentId || null,
  };
  const hasChanges = initialPlacement !== null && JSON.stringify(payload) !== JSON.stringify(initialPlacement);
  const selectedManager = managerById.get(managerEmployeeId);
  const departmentName = departments.find((department) => department._id === departmentId)?.name
    || (getDepartmentId(selectedManager?.department) === departmentId ? selectedManager?.department?.name : '')
    || (getDepartmentId(employee?.department) === departmentId ? employee?.department?.name : '')
    || 'No department';

  const handleManagerChange = (manager: Employee | string | null) => {
    setError(null);
    if (typeof manager === 'string') {
      setManagerEmployeeId('');
      setManagerUserId(null);
      setManagerName(manager);
      return;
    }
    setManagerEmployeeId(manager?._id || '');
    setManagerName(manager?.name || '');
    setManagerUserId(manager?.userId || null);
    setTeamName(manager ? manager.teamName || manager.department?.name || '' : employee?.teamName || '');
    setDepartmentId(getDepartmentId(manager ? manager.department : employee?.department));
  };

  const handleSave = async () => {
    if (busy || pendingRef.current || !hasChanges || !canEditPlacement || !employee) return;
    pendingRef.current = true;
    setPendingAction('save');
    setError(null);
    try {
      await onSavePlacement(payload);
      setInitialPlacement(payload);
    } catch (failure: any) {
      setError(failure?.data?.error || failure?.message || 'Couldn’t save these changes. Your edits are still here; try again.');
    } finally {
      pendingRef.current = false;
      setPendingAction(null);
    }
  };

  const handleRemove = async () => {
    if (busy || pendingRef.current || !employee?.canEdit) return;
    pendingRef.current = true;
    setPendingAction('remove');
    setError(null);
    try {
      await onRemove();
    } catch (failure: any) {
      setError(failure?.data?.error || failure?.message || 'Couldn’t remove this desk assignment. Try again in a moment.');
    } finally {
      pendingRef.current = false;
      setPendingAction(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy && !pendingRef.current) onClose(); }}
      aria-labelledby="employee-placement-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle component="div" id="employee-placement-header" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2 }}>
        {employee && <PersonAvatar name={employee.name} size={40} />}
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" id="employee-placement-title" variant="h3" sx={{ color: sbg.text.primary, overflowWrap: 'anywhere' }}>
            {employee?.name || 'Employee placement'}
          </Typography>
          {employee && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25, overflowWrap: 'anywhere' }}>
              {employee.email}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {employee && (
          <Box sx={{ display: 'grid', gap: 3 }}>
            <Box
              component="section"
              sx={{
                display: 'grid',
                gap: 1.5,
                p: 2,
                borderRadius: sbg.radius.lg,
                bgcolor: sbg.bg.surfaceSubtle,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <IconTile size={40}>
                  <Armchair />
                </IconTile>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="overline" component="p" sx={{ color: sbg.text.secondary }}>
                    Current desk
                  </Typography>
                  <Typography variant="h4" component="p" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                    {assignment ? deskLabel(assignment.desk) : 'No desk assigned'}
                  </Typography>
                  {deskFloorName && (
                    <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
                      {deskFloorName}
                    </Typography>
                  )}
                </Box>
                {!assignment && <StatusPill tone="neutral">Unassigned</StatusPill>}
              </Box>
              {assignment ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<LocateFixed size={16} />} onClick={onLocate} disabled={busy}>
                    Locate desk
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<ArrowLeftRight size={16} />} onClick={onChangeDesk} disabled={busy || !employee.canEdit}>
                    Change desk
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<UserMinus size={16} />}
                    onClick={handleRemove}
                    disabled={busy || !employee.canEdit}
                    aria-busy={pendingAction === 'remove' || undefined}
                    sx={{ ml: { sm: 'auto' } }}
                  >
                    {pendingAction === 'remove' ? 'Removing…' : 'Remove from desk'}
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
                  To seat them, drag their name from the roster onto a desk, or select an open desk on the floor plan.
                </Typography>
              )}
              {assignment && !employee.canEdit && (
                <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
                  You can view this desk but not change it.
                </Typography>
              )}
            </Box>

            <Box component="section" aria-labelledby="employee-placement-org-title" sx={{ display: 'grid', gap: 2 }}>
              <Box>
                <Typography id="employee-placement-org-title" component="h3" variant="h4" sx={{ color: sbg.text.primary }}>
                  Manager and team
                </Typography>
                {canEditPlacement ? (
                  <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
                    Choosing a manager fills in their team and department.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, color: sbg.text.secondary }}>
                    <Lock size={14} aria-hidden="true" />
                    <Typography variant="body2" component="p" sx={{ color: 'inherit' }}>
                      Only admins can change the manager and team.
                    </Typography>
                  </Box>
                )}
              </Box>
              <Autocomplete<Employee, false, false, true>
                freeSolo
                forcePopupIcon
                openOnFocus
                fullWidth
                size="small"
                clearText="Remove manager"
                slotProps={{ clearIndicator: { style: { visibility: 'visible' }, tabIndex: 0 } }}
                options={managerCandidates}
                filterOptions={filterManagers}
                value={selectedManager || managerName || null}
                inputValue={managerName}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                onChange={(_event, value) => handleManagerChange(value)}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') {
                    setManagerName(value);
                    setManagerEmployeeId('');
                    setManagerUserId(null);
                    setError(null);
                  }
                }}
                disabled={!canEditPlacement || busy}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option._id}>
                    <PersonCell
                      size={24}
                      name={option.name}
                      secondary={option.teamName || option.department?.name || option.email}
                    />
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Manager" placeholder="e.g. Maria Chen"
                    helperText={canEditPlacement ? 'Pick from the list, or type a name that isn’t listed.' : undefined} />
                )}
              />
              <TextField label="Team" value={teamName} fullWidth size="small"
                onChange={(event) => { setTeamName(event.target.value); setError(null); }}
                disabled={!canEditPlacement || busy} />
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, minHeight: 32 }}>
                  <Box component="dl" sx={{ m: 0, display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                    <Typography component="dt" variant="body1" sx={{ color: sbg.text.secondary }}>Department</Typography>
                    <Typography component="dd" variant="body1" sx={{ m: 0, color: sbg.text.primary, overflowWrap: 'anywhere' }}>{departmentName}</Typography>
                  </Box>
                  {canEditPlacement && <Button size="small" variant="text" onClick={() => setShowDepartment((value) => !value)} disabled={busy}
                    aria-expanded={showDepartment} aria-controls="placement-department" sx={{ flexShrink: 0 }}>
                    {showDepartment ? 'Hide department' : 'Change department'}
                  </Button>}
                </Box>
                <Collapse in={showDepartment} unmountOnExit>
                  <Box id="placement-department" sx={{ pt: 1.5 }}>
                    <FormControl fullWidth size="small" disabled={!canEditPlacement || busy}>
                      <InputLabel id="floorplan-department-label">Department</InputLabel>
                      <Select labelId="floorplan-department-label" value={departmentId} label="Department"
                        onChange={(event) => { setDepartmentId(event.target.value); setError(null); }}>
                        <MenuItem value="">No department</MenuItem>
                        {departments.map((department) => <MenuItem key={department._id} value={department._id}>{department.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                </Collapse>
              </Box>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ pt: 2, borderTop: `1px solid ${sbg.border.subtle}` }}>
        <Button variant="text" onClick={onClose} disabled={busy}>{canEditPlacement ? 'Cancel' : 'Close'}</Button>
        {canEditPlacement && <Button onClick={handleSave} disabled={busy || !employee || !hasChanges} variant="contained"
          aria-busy={pendingAction === 'save' || placementSaving || undefined} sx={{ minWidth: 128 }}>
          {pendingAction === 'save' || placementSaving ? 'Saving…' : 'Save changes'}
        </Button>}
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeDetailDialog;
